/**
 * Aplicar o kit de marca de um espaço na web: variáveis de cor, letra e logótipo.
 * Membros: ficheiros pedidos com a sessão; página pública e embed: endereços públicos.
 */
import { FONT_STACKS, readableAccent, softOf, textOn, type BrandSettings } from '@nolens/domain';
import { useEffect, useState, type CSSProperties } from 'react';
import { apiBlob } from './api';

export function brandStyle(b: BrandSettings | null | undefined, fontReady = true): CSSProperties {
  if (!b) return {};
  const font = b.font === 'custom' && !fontReady ? FONT_STACKS.inter : FONT_STACKS[b.font];
  const text = readableAccent(b.accent);
  // As classes do Tailwind usam --color-*, resolvidas em :root: redefinem-se aqui também.
  return {
    '--accent': b.accent,
    '--on-accent': textOn(b.accent),
    '--accent-text': text,
    '--accent-soft': softOf(b.accent),
    '--brand-product': text,
    '--color-accent': b.accent,
    '--color-on-accent': textOn(b.accent),
    '--color-accent-text': text,
    '--color-accent-soft': softOf(b.accent),
    fontFamily: font,
  } as CSSProperties;
}

const fonts = new Map<string, Promise<void>>();

/** Regista a letra da marca como «BrandFont» (uma vez por ficheiro). */
function loadFont(key: string, source: () => Promise<ArrayBuffer | string>): Promise<void> {
  let p = fonts.get(key);
  if (!p) {
    p = source().then(async (src) => {
      const face = new FontFace(
        'BrandFont',
        typeof src === 'string' ? `url(${JSON.stringify(src)})` : src,
      );
      await face.load();
      document.fonts.add(face);
    });
    p.catch(() => fonts.delete(key));
    fonts.set(key, p);
  }
  return p;
}

const logos = new Map<string, Promise<string>>();

/** Logótipo e letra de um espaço, para membros (pedidos com a sessão). */
export function useMemberBrandAssets(ws: string | undefined, b: BrandSettings | null | undefined) {
  const [state, setState] = useState<{ key: string; logo: string | null; fontReady: boolean }>({
    key: '',
    logo: null,
    fontReady: false,
  });
  // Só os valores que mudam os ficheiros: o objeto da marca é novo a cada desenho (ex.: a
  // pré-visualização na página da marca) e não pode ser dependência do efeito, senão
  // pede e desenha sem parar.
  const has = !!b;
  const logoVersion = b?.logoVersion ?? null;
  const fontVersion = b?.fontVersion ?? null;
  const fontChoice = b?.font;
  const key = `${ws}:${logoVersion ?? 0}:${fontVersion ?? 0}:${fontChoice}`;
  useEffect(() => {
    if (!ws || !has) return;
    let alive = true;
    const logoKey = `${ws}:${logoVersion}`;
    let logo: Promise<string | null> = Promise.resolve(null);
    if (logoVersion) {
      let lp = logos.get(logoKey);
      if (!lp) {
        lp = apiBlob(`/v1/workspaces/${ws}/brand/logo`).then((x) => URL.createObjectURL(x));
        lp.catch(() => logos.delete(logoKey));
        logos.set(logoKey, lp);
      }
      logo = lp;
    }
    const font =
      fontChoice === 'custom' && fontVersion
        ? loadFont(`${ws}:${fontVersion}`, () =>
            apiBlob(`/v1/workspaces/${ws}/brand/font`).then((x) => x.arrayBuffer()),
          ).then(
            () => true,
            () => false,
          )
        : Promise.resolve(false);
    void Promise.all([logo.catch(() => null), font]).then(
      ([l, f]) => alive && setState({ key, logo: l, fontReady: f }),
    );
    return () => {
      alive = false;
    };
  }, [ws, has, logoVersion, fontVersion, fontChoice, key]);
  return state.key === key ? state : { logo: null, fontReady: false };
}

/** Letra da marca na página pública (endereço público da API). */
export function usePublicFont(url: string | null): boolean {
  const [ready, setReady] = useState<string | null>(null);
  useEffect(() => {
    if (!url) return;
    let alive = true;
    loadFont(url, async () => url).then(
      () => alive && setReady(url),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [url]);
  return !!url && ready === url;
}
